'use client';

import {
  Badge,
  BodyDefault,
  Button,
  MetaDefault,
  MonoDefault,
  Skeleton,
} from '@terra/ui-design-system';
import { HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import {
  useTargetProfile,
  type TargetProfileFacts,
} from '../../hooks/dashboard/useTargetProfile';
import { DashboardStripCard } from './DashboardStripCard';
import { useOpenTargetSettings } from './useOpenTargetSettings';

const MAX_CHIPS = 2;
const PROFILE_FACT_KEYS = [
  'url',
  'environment',
  'techStack',
  'authMechanisms',
  'waf',
] as const;

export type ProfileFactKey = (typeof PROFILE_FACT_KEYS)[number];

type TargetProfileCardProps = {
  applicationId: string;
  factKeys?: readonly ProfileFactKey[];
};

export function TargetProfileCard({
  applicationId,
  factKeys = PROFILE_FACT_KEYS,
}: TargetProfileCardProps) {
  const t = useTranslations('home.targetProfile');
  const { facts, isLoading } = useTargetProfile(applicationId);
  const { openSettings, openSettingsContextMenu } = useOpenTargetSettings();

  if (!isLoading && !facts) {
    return null;
  }

  return (
    <DashboardStripCard
      title={t('title')}
      trailingContent={
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openSettings('scoping')}
          onContextMenu={(event) => openSettingsContextMenu(event, 'scoping')}
        >
          {t('openSettings')}
        </Button>
      }
      columns={factKeys.length >= 5 ? 5 : 4}
      cells={factKeys.map((key) => ({
        key,
        content: (
          <VStack gap={2} alignItems="flex-start" minW={0}>
            <MetaDefault color="fg.neutral.muted">
              {t(`facts.${key}`)}
            </MetaDefault>
            {facts ? (
              <ProfileFactValue factKey={key} facts={facts} />
            ) : (
              <Skeleton h={5} w={24} />
            )}
          </VStack>
        ),
      }))}
    />
  );
}

function ProfileFactValue({
  factKey,
  facts,
}: {
  factKey: ProfileFactKey;
  facts: TargetProfileFacts;
}) {
  const t = useTranslations('home.targetProfile');

  switch (factKey) {
    case 'url':
      return facts.url ? (
        <MonoDefault color="fg.neutral.default" truncate maxW="full">
          {facts.url}
        </MonoDefault>
      ) : (
        <NotSet />
      );
    case 'environment':
      return facts.environment ? (
        <BodyDefault color="fg.neutral.default">
          {facts.environment}
        </BodyDefault>
      ) : (
        <NotSet />
      );
    case 'techStack':
      return <FactChips values={facts.techStack} />;
    case 'authMechanisms':
      return <FactChips values={facts.authMechanisms} />;
    case 'waf':
      return facts.hasWaf === null ? (
        <NotSet />
      ) : (
        <BodyDefault color="fg.neutral.default">
          {t(facts.hasWaf ? 'wafYes' : 'wafNo')}
        </BodyDefault>
      );
  }
}

function FactChips({ values }: { values: string[] }): ReactNode {
  const t = useTranslations('home.targetProfile');

  if (values.length === 0) {
    return <NotSet />;
  }

  const hidden = values.length - MAX_CHIPS;

  return (
    <HStack gap={1} flexWrap="wrap" minW={0} maxW="full">
      {values.slice(0, MAX_CHIPS).map((value) => (
        <Badge
          key={value}
          variant="outline"
          size="sm"
          maxW="full"
          minW={0}
          title={value}
          css={{
            '& > *': { minWidth: 0, maxWidth: '100%' },
            '& > * > span': {
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        >
          {value}
        </Badge>
      ))}
      {hidden > 0 ? (
        <MetaDefault color="fg.neutral.muted">
          {t('more', { count: hidden })}
        </MetaDefault>
      ) : null}
    </HStack>
  );
}

function NotSet() {
  const t = useTranslations('home.targetProfile');

  return <BodyDefault color="fg.neutral.subtle">{t('notSet')}</BodyDefault>;
}
